import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { FiAward, FiUpload, FiTrash2, FiSave, FiCheck, FiPlus } from 'react-icons/fi';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

/**
 * The signer's professional credentials: seal image, signature image, and the
 * registration numbers printed on a sealed letter.
 *
 * GSWCC certifications are a list because one person commonly holds more than
 * one level at once — a Level II Design Professional who is also a Level IB
 * Certified Inspector — each with its own certification number.
 *
 * These are per-person rather than company-wide because a seal belongs to the
 * licensed surveyor, not the firm. Images are fetched as blobs — the endpoint
 * requires a bearer token that a plain <img src> would not send, which is also
 * what keeps a seal off a guessable public URL.
 */
// The three certification levels GSWCC issues. Free text stays available for
// anything that does not fit.
const GSWCC_LEVELS = [
  'Level IA Certified Personnel',
  'Level IB Certified Inspector',
  'Level II Design Professional',
];

function MyLicenseCredentials() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    credential_title: '',
    pls_license_number: '',
  });
  const [certs, setCerts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(false);
  const [previews, setPreviews] = useState({ seal: null, signature: null });
  const [busyKind, setBusyKind] = useState(null);
  const sealRef = useRef();
  const sigRef = useRef();
  const objectUrls = useRef([]);

  const token = localStorage.getItem('authToken');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!user) return;
    setForm({
      credential_title: user.credential_title || '',
      pls_license_number: user.pls_license_number || '',
    });
    setCerts(user.gswcc_certs || []);
  }, [user]);

  const loadImage = useCallback(async (kind, present) => {
    if (!user || !present) {
      setPreviews(prev => ({ ...prev, [kind]: null }));
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/team/${user.id}/${kind}`, {
        ...config, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      objectUrls.current.push(url);
      setPreviews(prev => ({ ...prev, [kind]: url }));
    } catch (err) {
      setPreviews(prev => ({ ...prev, [kind]: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    loadImage('seal', user?.has_seal);
    loadImage('signature', user?.has_signature);
  }, [loadImage, user?.has_seal, user?.has_signature]);

  useEffect(() => () => {
    objectUrls.current.forEach(URL.revokeObjectURL);
    objectUrls.current = [];
  }, []);

  const saveNumbers = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/team/${user.id}/license`,
        { ...form, gswcc_certs: certs.filter(c => (c.level || '').trim() || (c.number || '').trim()) },
        config,
      );
      await refreshUser();
      setSavedAt(true);
      setTimeout(() => setSavedAt(false), 2200);
    } catch (err) {
      alert('Could not save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (kind, file) => {
    if (!file) return;
    setBusyKind(kind);
    try {
      const form2 = new FormData();
      form2.append('image', file);
      await axios.post(`${API_URL}/team/${user.id}/${kind}`, form2, {
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' },
      });
      await refreshUser();
      await loadImage(kind, true);
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusyKind(null);
      if (kind === 'seal' && sealRef.current) sealRef.current.value = '';
      if (kind === 'signature' && sigRef.current) sigRef.current.value = '';
    }
  };

  const removeImage = async (kind) => {
    if (!window.confirm(`Remove your ${kind} image?`)) return;
    setBusyKind(kind);
    try {
      await axios.delete(`${API_URL}/team/${user.id}/${kind}`, config);
      await refreshUser();
      setPreviews(prev => ({ ...prev, [kind]: null }));
    } catch (err) {
      alert('Could not remove: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusyKind(null);
    }
  };

  const addCert = () => setCerts([
    ...certs,
    { level: GSWCC_LEVELS[2], number: '', show_on_letters: true },
  ]);
  const updateCert = (i, key, val) =>
    setCerts(certs.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)));
  const removeCert = (i) => setCerts(certs.filter((_, idx) => idx !== i));

  if (!user) return null;

  const labelStyle = {
    display: 'block', fontSize: '11px', textTransform: 'uppercase',
    color: '#888', fontWeight: 600, marginBottom: '4px', letterSpacing: '.03em',
  };
  const inputStyle = {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: '8px', fontSize: '15px', boxSizing: 'border-box',
  };

  const imageSlot = (kind, title, hint, inputRef, preview) => (
    <div style={{ flex: 1, minWidth: '180px' }}>
      <label style={labelStyle}>{title}</label>
      <div style={{
        border: '1px dashed #ddd', borderRadius: '10px', padding: '10px',
        background: '#fafafa', textAlign: 'center', minHeight: '104px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px',
      }}>
        {preview ? (
          <img
            src={preview}
            alt={title}
            style={{ maxHeight: '76px', maxWidth: '100%', objectFit: 'contain' }}
          />
        ) : (
          <div style={{ fontSize: '12px', color: '#aaa' }}>{hint}</div>
        )}
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busyKind === kind}
            style={{
              padding: '5px 10px', fontSize: '12px', border: '1px solid #ddd',
              borderRadius: '6px', background: 'white', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: '4px',
            }}
          >
            <FiUpload size={11} /> {preview ? 'Replace' : 'Upload'}
          </button>
          {preview && (
            <button
              type="button"
              onClick={() => removeImage(kind)}
              disabled={busyKind === kind}
              style={{
                padding: '5px 9px', fontSize: '12px', border: '1px solid #ddd',
                borderRadius: '6px', background: 'white', cursor: 'pointer', color: '#d33', lineHeight: 0,
              }}
            >
              <FiTrash2 size={12} />
            </button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={(e) => uploadImage(kind, e.target.files[0])}
        style={{ display: 'none' }}
      />
    </div>
  );

  return (
    <div style={{
      backgroundColor: 'white', borderRadius: '16px', padding: '20px',
      marginBottom: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: '#666', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FiAward size={18} /> Professional Seal &amp; License
      </h3>
      <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#999', lineHeight: 1.5 }}>
        Used when you tick &ldquo;Apply my seal&rdquo; on a letter. A transparent PNG reproduces best.
      </p>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
        {imageSlot('seal', 'Seal', 'No seal uploaded', sealRef, previews.seal)}
        {imageSlot('signature', 'Signature', 'No signature uploaded', sigRef, previews.signature)}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div>
          <label style={labelStyle}>Title</label>
          <input
            style={inputStyle}
            value={form.credential_title}
            onChange={(e) => setForm({ ...form, credential_title: e.target.value })}
            placeholder="Professional Land Surveyor"
          />
        </div>
        <div>
          <label style={labelStyle}>Georgia P.L.S. No.</label>
          <input
            style={inputStyle}
            value={form.pls_license_number}
            onChange={(e) => setForm({ ...form, pls_license_number: e.target.value })}
            placeholder="e.g. 3142"
          />
        </div>

        {/* GSWCC certifications — one row per level held */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>GSWCC Certifications</label>
            <button
              type="button"
              onClick={addCert}
              style={{
                padding: '4px 10px', background: 'white', color: '#374151',
                border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer',
                fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '4px',
              }}
            >
              <FiPlus size={12} /> Add
            </button>
          </div>
          <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>
            Georgia Soil and Water Conservation Commission. Untick a certification to keep it
            off your letters.
          </div>

          {certs.length === 0 ? (
            <div style={{ fontSize: '13px', color: '#aaa', fontStyle: 'italic' }}>
              None added.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {certs.map((c, i) => (
                <div
                  key={i}
                  style={{
                    border: '1px solid #eee', borderRadius: '8px', padding: '10px',
                    background: c.show_on_letters ? 'white' : '#fafafa',
                  }}
                >
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <input
                      list={`gswcc-levels-${i}`}
                      style={{ ...inputStyle, flex: '2 1 200px' }}
                      value={c.level || ''}
                      onChange={(e) => updateCert(i, 'level', e.target.value)}
                      placeholder="Level"
                    />
                    <datalist id={`gswcc-levels-${i}`}>
                      {GSWCC_LEVELS.map(l => <option key={l} value={l} />)}
                    </datalist>
                    <input
                      style={{ ...inputStyle, flex: '1 1 130px' }}
                      value={c.number || ''}
                      onChange={(e) => updateCert(i, 'number', e.target.value)}
                      placeholder="Cert. No."
                    />
                    <button
                      type="button"
                      onClick={() => removeCert(i)}
                      title="Remove certification"
                      style={{
                        padding: '10px 11px', background: 'white', color: '#d33',
                        border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', lineHeight: 0,
                      }}
                    >
                      <FiTrash2 size={13} />
                    </button>
                  </div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '13px', color: '#555', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={c.show_on_letters !== false}
                      onChange={(e) => updateCert(i, 'show_on_letters', e.target.checked)}
                    />
                    Show on sealed letters
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <button
        onClick={saveNumbers}
        disabled={saving}
        style={{
          marginTop: '16px', width: '100%', padding: '13px',
          backgroundColor: savedAt ? '#16a34a' : '#0f2942', color: 'white', border: 'none',
          borderRadius: '10px', fontSize: '15px', fontWeight: 600,
          cursor: saving ? 'wait' : 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'center', gap: '8px',
        }}
      >
        {savedAt ? <><FiCheck size={16} /> Saved</> : <><FiSave size={16} /> {saving ? 'Saving...' : 'Save License Details'}</>}
      </button>
    </div>
  );
}

export default MyLicenseCredentials;
